// Units, on the client.
//
// # THIS FILE DELIBERATELY CONTAINS NO CONVERSION
//
// There is no pound constant here and there must never be one. The server
// stores kilograms and converts at the boundary in both directions:
//
//   - reading — the API sends `weight`/`load` already in the reader's unit,
//     already rounded to a loadable increment, alongside the `unit` it is in.
//     The client renders the pair.
//   - writing — the client sends the number the user typed plus the unit it
//     was typed in. The server converts before validating.
//
// So the client never needs the arithmetic, and adding it would create a sixth
// client-side mirror of a backend rule (see docs/AUDIT-2026-07.md §4) — one
// that goes silently wrong if the Go constant ever changes. Everything here is
// presentation.

/** The unit to assume before /preferences has answered. */
export const DEFAULT_UNIT = 'kg';

/**
 * Format a load with its unit, or as bodyweight.
 *
 * Zero is not "no load" — it is bodyweight work, which is most of the calisthenic
 * volume in any programme, so it gets a word rather than "0 kg". Negative is
 * assisted work: a band or a machine taking load off.
 *
 * @param {number|null|undefined} value load in `unit`
 * @param {string} unit
 * @returns {string}
 */
export function formatLoad(value, unit = DEFAULT_UNIT) {
  if (value === null || value === undefined || value === '') return '';
  const load = Number(value);
  if (!Number.isFinite(load)) return '';

  if (load === 0) return 'Bodyweight';
  if (load < 0) return `${formatNumber(Math.abs(load))} ${unit} assisted`;
  return `${formatNumber(load)} ${unit}`;
}

/**
 * Trim the trailing zeros a float picks up in transit.
 *
 * The server rounds to a loadable increment before sending, so 102.5 arrives as
 * 102.5 — but 102.0 arrives as 102, and JSON round-tripping can still produce
 * "102.50". Rendering a plate weight as "102.50 kg" reads like false precision.
 */
export function formatNumber(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return String(Number(n.toFixed(2)));
}

/**
 * The label for a load input, e.g. "Load (kg)".
 *
 * Centralised because the unit was hardcoded into three separate strings — a
 * field label, a screen-reader label and a history line — and a user switching
 * units would have had to be right in all three.
 */
export function loadLabel(unit = DEFAULT_UNIT, prefix = 'Load') {
  return `${prefix} (${unit})`;
}

/**
 * The spoken label for a set's load input.
 */
export function loadInputAriaLabel(exerciseName, setNumber, unit = DEFAULT_UNIT) {
  return `${exerciseName} set ${setNumber} load in ${unitName(unit)}, leave blank for bodyweight`;
}

/**
 * The unit spelled out, for screen readers. "kg" is read as letters by most
 * screen readers, which is worse than the word.
 */
export function unitName(unit) {
  if (unit === 'lb') return 'pounds';
  if (unit === 'kg') return 'kilograms';
  return String(unit ?? '');
}

/**
 * Render the prescribed intensity as a short label, or null when none was
 * prescribed.
 *
 * Mirrors models.Prescription: absolute load, %1RM, RPE and RIR are independent
 * and any combination may be present. `load` is already in `unit` — this does
 * not read `loadKg`, which is the canonical value and is only ever the right
 * thing to display for a reader who happens to work in kilograms.
 */
export function formatIntensity({ load, unit, percentOneRM, percentLoad, rpe, rir } = {}) {
  const parts = [];
  if (load !== null && load !== undefined) {
    parts.push(`${formatNumber(load)} ${unit ?? DEFAULT_UNIT}`);
  }
  if (percentOneRM !== null && percentOneRM !== undefined) {
    // The percentage AND what it works out to, when the reader has a max.
    //
    // The server resolves it (MAX-5) — against this reader's max, recomputed on
    // every read. Showing only "80% 1RM" leaves the athlete to do the arithmetic
    // the whole feature exists to do for them, which is what this said before.
    //
    // Both are shown rather than the load alone: the percentage is what the
    // coach prescribed and what stays true across a retest, and a number with no
    // percentage beside it is indistinguishable from an absolute load.
    parts.push(
      percentLoad !== null && percentLoad !== undefined
        ? `${percentOneRM}% 1RM (${formatNumber(percentLoad)} ${unit ?? DEFAULT_UNIT})`
        : `${percentOneRM}% 1RM`
    );
  }
  if (rpe !== null && rpe !== undefined) parts.push(`RPE ${rpe}`);
  if (rir !== null && rir !== undefined) parts.push(`${rir} RIR`);
  return parts.length > 0 ? parts.join(' · ') : null;
}
